// ScrollAnimator V1.2
// reusable animated scroll engine with built-in wheel/touch override listeners

// init:
// scrollAnimator = new ScrollAnimator();
// scrollAnimator.init();

// usage:
// call animateScrollTop/animateScrollLeft for animated scroll movement

// required properties:
// none

// possible properties:
// durationFactor = 35: larger means slower animation
// minDuration = 400: minimum animation duration in ms
// allowOverride = true: allow user input (wheel/touch) to interrupt animation
// overrideThreshold = 120: wheel delta threshold before override kicks in
// touchOverrideIncrement = 12: accumulated touch delta step for override
// onStart = '': callback fired when animation starts
// onStop = '': callback fired when animation stops
// beforeAnimate = '': callback before animation frame loop starts
// afterAnimate = '': callback after animation frame loop completes

// methods:
// scrollAnimator.init()
// scrollAnimator.destroy()
// scrollAnimator.setDurationFactor(35)
// scrollAnimator.isAnimating()
// scrollAnimator.cancel()
// scrollAnimator.setHooks({beforeAnimate, afterAnimate})
// scrollAnimator.animateScrollTop(scroll, {element, useMax, onDone})
// scrollAnimator.animateScrollLeft(scroll, {element, useMax, onDone})

// changelog:
// 1.0 initial version, extracted from scroll helpers and renamed for clarity
// 1.1 make methods private for clearer distinction
// 1.2 add cancel() to stop animation programmatically

export default class ScrollAnimator {
  #isScrolling;
  #overrideTotal;
  #didOverride;
  #initialized;
  #boundHandleWheel;
  #boundHandleTouchMove;

  constructor({
    durationFactor = 35,
    minDuration = 400,
    allowOverride = true,
    overrideThreshold = 120,
    touchOverrideIncrement = 12,
    onStart = '',
    onStop = '',
    beforeAnimate = '',
    afterAnimate = '',
  } = {}) {
    this.durationFactor = durationFactor;
    this.minDuration = minDuration;
    this.allowOverride = allowOverride;
    this.overrideThreshold = overrideThreshold;
    this.touchOverrideIncrement = touchOverrideIncrement;
    this.onStart = onStart;
    this.onStop = onStop;
    this.beforeAnimate = beforeAnimate;
    this.afterAnimate = afterAnimate;

    this.#isScrolling = false;
    this.#overrideTotal = 0;
    this.#didOverride = false;
    this.#initialized = false;

    this.#boundHandleWheel = this.#handleWheel.bind(this);
    this.#boundHandleTouchMove = this.#handleTouchMove.bind(this);
  }

  init = () => {
    if (this.#initialized || !document.body) return;

    document.body.addEventListener('wheel', this.#boundHandleWheel);
    document.body.addEventListener('touchmove', this.#boundHandleTouchMove, {passive: false});

    this.#initialized = true;
  }

  destroy = () => {
    if (!this.#initialized || !document.body) return;

    document.body.removeEventListener('wheel', this.#boundHandleWheel);
    document.body.removeEventListener('touchmove', this.#boundHandleTouchMove);

    this.#initialized = false;
  }

  setDurationFactor = (durationFactor) => {
    this.durationFactor = durationFactor;
  }

  setHooks = ({
    beforeAnimate,
    afterAnimate,
  } = {}) => {
    if (typeof beforeAnimate !== 'undefined') this.beforeAnimate = beforeAnimate;
    if (typeof afterAnimate !== 'undefined') this.afterAnimate = afterAnimate;
  }

  isAnimating = () => {
    return this.#isScrolling;
  }

  cancel = () => {
    if (this.#isScrolling) this.#didOverride = true;
  }

  animateScrollTop = (scroll, {
    element = document.body,
    useMax = true,
    onDone = '',
  } = {}) => {
    this.#animate({
      scroll,
      direction: 'top',
      element,
      useMax,
      onDone,
    });
  }

  animateScrollLeft = (scroll, {
    element = document.body,
    useMax = true,
    onDone = '',
  } = {}) => {
    this.#animate({
      scroll,
      direction: 'left',
      element,
      useMax,
      onDone,
    });
  }

  #animate({
    scroll,
    direction = 'top',
    element = document.body,
    useMax = true,
    onDone = '',
    beforeAnimate = '',
    afterAnimate = '',
  } = {}) {
    const originalElement = element;
    const shouldTrackScrollState = originalElement?.tagName === 'BODY';

    const beforeCallback = typeof beforeAnimate === 'function' ? beforeAnimate : this.beforeAnimate;
    if (shouldTrackScrollState && typeof beforeCallback === 'function') {
      beforeCallback({direction, element: originalElement, originalElement, scroll});
    }

    let size;
    let scrollSize;

    if (element.tagName === 'BODY') {
      size = direction === 'top' ? window.innerHeight : window.innerWidth;
      scrollSize = direction === 'top' ? document.documentElement.scrollHeight : document.documentElement.scrollWidth;
      element = document.documentElement;
    } else {
      size = direction === 'top' ? element.offsetHeight : element.offsetWidth;
      scrollSize = direction === 'top' ? element.scrollHeight : element.scrollWidth;
    }

    if (useMax) {
      const maxScroll = scrollSize - size;
      if (scroll > maxScroll) scroll = maxScroll;
      if (scroll < 0) scroll = 0;
    }

    const delay = this.#isScrolling ? 50 : 0;
    if (this.#isScrolling) this.#didOverride = true;

    setTimeout(() => this.#scrollToPosition({
      to: scroll,
      direction,
      element,
      onDone,
      afterAnimate,
      originalElement,
      shouldTrackScrollState,
    }), delay);
  }

  #scrollToPosition({
    to,
    direction = 'top',
    element,
    onDone = '',
    afterAnimate = '',
    originalElement = element,
    shouldTrackScrollState = false,
  } = {}) {
    this.#overrideTotal = 0;
    this.#didOverride = false;

    let start;
    if (element == document.documentElement) {
      start = direction === 'top' ? window.scrollY || element.scrollTop : window.scrollX || element.scrollLeft;
    } else {
      start = direction === 'top' ? element.scrollTop : element.scrollLeft;
    }

    const change = to - start;
    if (change < 1 && change > -1) {
      const afterCallback = typeof afterAnimate === 'function' ? afterAnimate : this.afterAnimate;
      if (shouldTrackScrollState && typeof afterCallback === 'function') {
        afterCallback({direction, element, originalElement, to, overridden: false});
      }

      if (typeof onDone === 'function') onDone(element);
      return;
    }

    if (shouldTrackScrollState) {
      this.#isScrolling = true;
      if (typeof this.onStart === 'function') this.onStart(shouldTrackScrollState);
    }

    let duration = Math.sqrt(Math.abs(change)) * this.durationFactor;
    if (duration < this.minDuration) duration = this.minDuration;

    const increment = 15;
    let currentTime = 0;
    const easeInOutQuart = t => (t < 0.5) ? (8 * (t ** 4)) : (1 - (8 * ((--t) * (t ** 3))));

    const done = ({overridden = false} = {}) => {
      if (shouldTrackScrollState) {
        this.#isScrolling = false;
        if (typeof this.onStop === 'function') this.onStop(shouldTrackScrollState);
      }

      const afterCallback = typeof afterAnimate === 'function' ? afterAnimate : this.afterAnimate;
      if (shouldTrackScrollState && typeof afterCallback === 'function') {
        afterCallback({direction, element, originalElement, to, overridden});
      }

      if (typeof onDone === 'function') onDone(element);
    };

    const animate = () => {
      currentTime += increment;
      const val = easeInOutQuart(currentTime / duration);
      const currentScroll = start + (change * val);

      if (direction === 'top') {
        element.scrollTo({top: currentScroll});
      } else {
        element.scrollTo({left: currentScroll});
      }

      if (currentTime < duration) {
        if (shouldTrackScrollState && this.#didOverride && this.allowOverride) {
          const wasOverridden = true;
          this.#didOverride = false;
          done({overridden: wasOverridden});
        } else {
          setTimeout(animate, increment);
        }
      } else {
        if (direction === 'top') {
          element.scrollTo({top: to});
        } else {
          element.scrollTo({left: to});
        }

        done();
      }
    };

    animate();
  }

  #handleWheel(event) {
    if (!this.#isScrolling) return;

    if (this.allowOverride) {
      const absDeltaY = Math.abs(event.deltaY || 0);
      const absDeltaX = Math.abs(event.deltaX || 0);
      this.#overrideTotal += absDeltaY > absDeltaX ? absDeltaY : absDeltaX;

      if (this.#overrideTotal > this.overrideThreshold) this.#didOverride = true;
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  #handleTouchMove(event) {
    if (!this.#isScrolling) return;

    if (this.allowOverride) {
      this.#overrideTotal += this.touchOverrideIncrement;

      if (this.#overrideTotal > this.overrideThreshold) this.#didOverride = true;
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
